'use client';

import { useState } from 'react';

// The book's plans, in the order they appear on the branch point. Keep the
// subtitles in sync with PLAN_SUBTITLES in planSubtitles.ts.
const PLANS: { id: string; subtitle: string }[] = [
  { id: 'A', subtitle: 'The Deal' },
  { id: 'B', subtitle: 'Sabotage' },
  { id: 'C', subtitle: 'The Slowdown' },
  { id: 'D', subtitle: 'The Race' },
  { id: 'S', subtitle: 'Shutdown' },
];

// The canonical working configuration — the one the print layout is being
// edited against — rendered as the big featured card above the grid.
const FEATURED_PRESET = {
  label: 'Scenario + alternative plans',
  detail: 'Plan A · other plans as appendices · no supplements',
  href: '/print/scenario?plan=A&supplements=0&altplans=1',
};

// One-click links for the other configurations people reach for.
const PRESETS: { label: string; detail: string; href: string }[] = [
  {
    label: 'The complete book',
    detail: 'Plan A · supplements',
    href: '/print/scenario?plan=A',
  },
  {
    label: 'Everything',
    detail: 'Plan A · other plans as appendices · supplements',
    href: '/print/scenario?plan=A&altplans=1',
  },
  {
    label: 'Scenario only',
    detail: 'Plan A · no supplements — fast',
    href: '/print/scenario?plan=A&supplements=0',
  },
  {
    label: 'Bound-book edition',
    detail: 'Plan A · supplements · alternating margins + sidenote gutters',
    href: '/print/scenario?plan=A&book=1',
  },
];

export function PrintIndex({
  supplements,
}: {
  supplements: { slug: string; title: string }[];
}) {
  // Builder defaults mirror FEATURED_PRESET.
  const [plan, setPlan] = useState('A');
  const [includeSupplements, setIncludeSupplements] = useState(false);
  const [altPlans, setAltPlans] = useState(true);
  const [bookMargins, setBookMargins] = useState(false);

  const qs = new URLSearchParams({ plan });
  if (!includeSupplements) qs.set('supplements', '0');
  if (altPlans) qs.set('altplans', '1');
  if (bookMargins) qs.set('book', '1');
  const builderHref = `/print/scenario?${qs.toString()}`;

  return (
    <div className="min-h-screen bg-[#fffff8] text-[#111] font-['et-book','Palatino_Linotype',Palatino,Georgia,serif]">
      <div className="mx-auto max-w-[680px] px-6 py-14">
        <div className="font-['Gill_Sans','Gill_Sans_MT','Trebuchet_MS',sans-serif] text-[13px] font-semibold uppercase tracking-[0.12em] text-[#8b0000]">
          The AI 2040 Scenario
        </div>
        <h1 className="mt-2 font-['Gill_Sans','Gill_Sans_MT','Trebuchet_MS',sans-serif] text-[44px] font-semibold leading-none">
          Print editions
        </h1>
        <p className="mt-4 max-w-[54ch] text-[17px] leading-snug">
          Every page below is a live print layout: open one, wait for the
          &ldquo;preparing globe&rdquo; badge to clear, and print it with ⌘P
          (use Chrome — the pagination relies on Chromium&rsquo;s print
          engine). The result is identical to the shipped PDFs.
        </p>

        {/* ── The common configurations ────────────────────────────────── */}
        <h2 className="mt-12 border-b border-black/20 pb-1.5 font-['Gill_Sans','Gill_Sans_MT','Trebuchet_MS',sans-serif] text-[15px] font-semibold uppercase tracking-[0.08em]">
          Common configurations
        </h2>
        <a
          href={FEATURED_PRESET.href}
          className="group mt-4 block rounded-sm border-2 border-[#8b0000] bg-[#8b0000]/[0.03] px-5 py-4 no-underline hover:bg-[#8b0000]/[0.08]"
        >
          <div className="text-[24px] leading-tight text-[#8b0000]">
            {FEATURED_PRESET.label}
          </div>
          <div className="mt-1 font-[Menlo,monospace] text-[11px] text-[#111]">
            {FEATURED_PRESET.detail}
          </div>
        </a>
        <div className="mt-2.5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {PRESETS.map((preset) => (
            <a
              key={preset.label}
              href={preset.href}
              className="group block rounded-sm border-[1.5px] border-[#111] px-4 py-3 no-underline hover:border-[#8b0000] hover:bg-[#8b0000]/[0.04]"
            >
              <div className="text-[19px] leading-tight text-[#111] group-hover:text-[#8b0000]">
                {preset.label}
              </div>
              <div className="mt-1 font-[Menlo,monospace] text-[10.5px] text-[#111]">
                {preset.detail}
              </div>
            </a>
          ))}
        </div>

        {/* ── The full builder ─────────────────────────────────────────── */}
        <h2 className="mt-12 border-b border-black/20 pb-1.5 font-['Gill_Sans','Gill_Sans_MT','Trebuchet_MS',sans-serif] text-[15px] font-semibold uppercase tracking-[0.08em]">
          Build your own
        </h2>
        <div className="mt-4 flex flex-col gap-5">
          <div>
            <div className="font-[Menlo,monospace] text-[11px] uppercase tracking-wide">
              Main scenario
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {PLANS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlan(p.id)}
                  className={`cursor-pointer rounded-sm border-[1.5px] px-3 py-1.5 text-left font-['et-book',Georgia,serif] ${
                    plan === p.id
                      ? 'border-[#8b0000] bg-[#8b0000] text-[#fffff8]'
                      : 'border-[#111] bg-transparent text-[#111] hover:border-[#8b0000] hover:text-[#8b0000]'
                  }`}
                >
                  <span className="text-[17px]">Plan {p.id}</span>
                  <span className="ml-1.5 text-[13px] opacity-80">
                    {p.subtitle}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {(
              [
                {
                  label: 'Include the supplements as back-matter',
                  checked: includeSupplements,
                  onChange: setIncludeSupplements,
                },
                {
                  label: 'Include the other plans as appendices',
                  checked: altPlans,
                  onChange: setAltPlans,
                },
                {
                  label: 'Bound-book layout (alternating margins and sidenote gutters)',
                  checked: bookMargins,
                  onChange: setBookMargins,
                },
              ] as const
            ).map((toggle) => (
              <label
                key={toggle.label}
                className="flex cursor-pointer items-baseline gap-2.5 text-[17px]"
              >
                <input
                  type="checkbox"
                  checked={toggle.checked}
                  onChange={(e) => toggle.onChange(e.target.checked)}
                  className="translate-y-[1px] accent-[#8b0000]"
                />
                {toggle.label}
              </label>
            ))}
          </div>

          <a
            href={builderHref}
            className="inline-block self-start rounded-sm bg-[#8b0000] px-5 py-2.5 font-['Gill_Sans','Gill_Sans_MT','Trebuchet_MS',sans-serif] text-[16px] font-semibold text-[#fffff8] no-underline hover:bg-[#6d0000]"
          >
            Open print view&ensp;→
          </a>
          <div className="-mt-3 font-[Menlo,monospace] text-[10.5px] text-[#111]">
            {builderHref}
          </div>
        </div>

        {/* ── Bookbinding cover wrap ───────────────────────────────────── */}
        <h2 className="mt-12 border-b border-black/20 pb-1.5 font-['Gill_Sans','Gill_Sans_MT','Trebuchet_MS',sans-serif] text-[15px] font-semibold uppercase tracking-[0.08em]">
          Bookbinding cover wrap
        </h2>
        <p className="mt-3 max-w-[54ch] text-[15px] leading-snug">
          One wide sheet — back cover, spine, front cover — for wrapping a
          bound copy. The spine is sized from the book&rsquo;s page count
          (read from the checked-in PDF; override with <code>?pages=</code>)
          and the paper caliper (<code>?paper=</code>, mm per sheet, default
          0.125).
        </p>
        <a
          href="/print/cover?plan=A&supplements=0&altplans=1"
          className="group mt-3 block rounded-sm border-[1.5px] border-[#111] px-4 py-3 no-underline hover:border-[#8b0000] hover:bg-[#8b0000]/[0.04]"
        >
          <div className="text-[19px] leading-tight text-[#111] group-hover:text-[#8b0000]">
            Cover wrap
          </div>
          <div className="mt-1 font-[Menlo,monospace] text-[10.5px] text-[#111]">
            Plan A · other plans as appendices · no supplements
          </div>
        </a>

        {/* ── Standalone supplements ───────────────────────────────────── */}
        <h2 className="mt-12 border-b border-black/20 pb-1.5 font-['Gill_Sans','Gill_Sans_MT','Trebuchet_MS',sans-serif] text-[15px] font-semibold uppercase tracking-[0.08em]">
          Standalone supplements
        </h2>
        <ul className="mt-3 columns-1 gap-x-10 sm:columns-2 [&_li]:mb-1.5">
          {supplements.map((s) => (
            <li key={s.slug} className="break-inside-avoid text-[16px] leading-snug">
              <a
                href={`/print/${s.slug}`}
                className="text-[#111] no-underline hover:text-[#8b0000] hover:underline"
              >
                {s.title}
              </a>
            </li>
          ))}
        </ul>

        <p className="mt-12 border-t border-black/20 pt-3 font-[Menlo,monospace] text-[10.5px] leading-relaxed text-[#111]">
          Scripted rendering: `npm run pdf:scenario -- A [no-supps] [book]
          [alt-plans]` · `npm run pdf:supplement -- &lt;slug&gt; [book]` ·
          `npm run pdf:cover -- A [no-supps] [alt-plans] [paper=0.125]
          [pages=N]`. In
          dev, /pdf-test previews the exact PDF pipeline output. `book`
          layouts alternate the sidenote gutter per page face in a single
          render — a plain ⌘P of a ?book=1 view produces the finished book.
        </p>
      </div>
    </div>
  );
}
